import './ImpactPicker.scss'
import { useEffect, useRef, useState } from 'react'
import { applyImpact, formatImpact, parseImpactInput, QUICK_IMPACTS } from '../../Impact'
import { formatCurrency } from '../../Helpers'

interface Props {
    value: Impact
    onChange: (impact: Impact) => void
    disabled?: boolean
    // Montant de l'opération : permet la saisie en euros et l'aperçu de la part comptée.
    amountCents?: number
}

// Impact en un clic : 100 · ½ · 0, et « … » pour une autre part (⅓, 25 %, 12,50 €).
// Une part hors des choix rapides s'affiche à la place des points de suspension.
const ImpactPicker = ({ value, onChange, disabled, amountCents }: Props) => {
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)
    const isQuick = QUICK_IMPACTS.some(({ impact }) => impact === value)

    useEffect(() => {
        if(!open) {
            return
        }
        const close = (e: MouseEvent) => {
            if(!ref.current?.contains(e.target as Node)) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', close)
        return () => document.removeEventListener('mousedown', close)
    }, [open])

    const pick = (impact: Impact) => {
        setOpen(false)
        if(impact !== value) {
            onChange(impact)
        }
    }

    return <div className="impact-picker" ref={ref}>
        <div className="segments">
            { QUICK_IMPACTS.map(({ impact, label }) => <button
                type="button" key={impact} disabled={disabled}
                className={impact === value ? 'on' : ''}
                title={formatImpact(impact)}
                onClick={() => pick(impact)}>{ label }</button>) }
            <button
                type="button" disabled={disabled}
                className={`custom ${isQuick ? '' : 'on'}`}
                title={isQuick ? 'Autre part' : `${formatImpact(value)} · changer`}
                onClick={() => setOpen(!open)}>{ isQuick ? '…' : formatImpact(value) }</button>
        </div>
        { open && <CustomImpact value={value} amountCents={amountCents} onPick={pick} onCancel={() => setOpen(false)}/> }
    </div>
}

const CustomImpact = ({ value, amountCents, onPick, onCancel }: { value: Impact, amountCents?: number, onPick: (impact: Impact) => void, onCancel: () => void }) => {
    const [text, setText] = useState('')
    const parsed = text.trim() === '' ? null : parseImpactInput(text, amountCents)
    const withEuros = amountCents !== undefined && amountCents !== 0

    const submit = (e: React.FormEvent) => {
        e.preventDefault()
        if(parsed && 'impact' in parsed) {
            onPick(parsed.impact)
        }
    }

    return <form className="impact-popover tl-card" onSubmit={submit}>
        <label htmlFor="custom-impact">Autre part</label>
        <input
            id="custom-impact" autoFocus autoComplete="off"
            placeholder={withEuros ? '1/3, 25 % ou 12,50 €' : '1/3 ou 25 %'}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if(e.key === 'Escape') { onCancel() } }}/>
        <div className={`hint ${parsed && 'error' in parsed ? 'error' : ''}`}>
            { parsed === null
                ? <>Actuellement <b>{ formatImpact(value) }</b>. Fraction, pourcentage{ withEuros && <>, ou montant en euros sur { formatCurrency(Math.abs(amountCents)) }</> }</>
                : 'error' in parsed
                    ? parsed.error
                    : <>= <b>{ formatImpact(parsed.impact) }</b>{ parsed.detail && ` (${parsed.detail})` }
                        { withEuros && <> · compté <b>{ formatCurrency(Math.round(applyImpact(amountCents, parsed.impact))) }</b></> } · Entrée pour valider</> }
        </div>
    </form>
}

export default ImpactPicker
